import { SaveIcon } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { MessageTemplate } from "../types";

const TEMPLATE_VARIABLES = [
  { label: "First name", value: "{{first_name}}" },
  { label: "Last name", value: "{{last_name}}" },
  { label: "Company", value: "{{company_name}}" },
  { label: "Title", value: "{{title}}" },
];

type MessageTemplateFormData = Pick<
  MessageTemplate,
  "name" | "body" | "channel"
>;

type MessageTemplateDialogProps = {
  open: boolean;
  template?: MessageTemplateFormData;
  title: string;
  onSubmit(data: MessageTemplateFormData): Promise<void>;
  onClose(): void;
};

export function MessageTemplateDialog({
  open,
  template,
  title,
  onClose,
  onSubmit,
}: MessageTemplateDialogProps) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState<"sms" | "whatsapp">("sms");
  const [disabled, setDisabled] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleClose = () => {
    setDisabled(false);
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit({ name, body, channel });
    setDisabled(true);
    setName("");
    setBody("");
    setChannel("sms");
    handleClose();
  };

  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.substring(0, start) + variable + body.substring(end);
    setBody(newBody);

    // Restore cursor position after the inserted variable
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + variable.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  useEffect(() => {
    setName(template?.name ?? "");
    setBody(template?.body ?? "");
    setChannel(template?.channel ?? "sms");
  }, [template]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Name</Label>
              <Input
                id="template-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Welcome message"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-body">Message body</Label>
              <Textarea
                id="template-body"
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi {{first_name}}, ..."
                rows={5}
                required
              />
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-muted-foreground mr-1 self-center">
                  Insert:
                </span>
                {TEMPLATE_VARIABLES.map((v) => (
                  <Button
                    key={v.value}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-6"
                    onClick={() => insertVariable(v.value)}
                  >
                    {v.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Default channel</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="sms"
                    checked={channel === "sms"}
                    onChange={() => setChannel("sms")}
                    className="accent-primary"
                  />
                  <span className="text-sm">SMS</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="whatsapp"
                    checked={channel === "whatsapp"}
                    onChange={() => setChannel("whatsapp")}
                    className="accent-primary"
                  />
                  <span className="text-sm">WhatsApp</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="outline" disabled={disabled} type="submit">
              <SaveIcon className="h-4 w-4" />
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
